import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { NAV_ITEMS } from "@/config/site";
import { AccessLink } from "@/components/AccessLink";
import SectionHeading from "@/components/SectionHeading";
import { IconByName } from "@/components/icons";
import { openMaterialsModal } from "@/components/MaterialsModal";
import { scrollToSection } from "@/lib/scroll";

function QuickCard({ item, index }) {
  if (item.opensMaterials) {
    return (
      <button
        type="button"
        data-testid={`quick-access-card-${item.id}`}
        onClick={openMaterialsModal}
        className="group relative flex h-full w-full flex-col rounded-2xl border border-white/10 bg-[#0d121c]/70 p-6 text-left shadow-[0_4px_24px_rgba(0,0,0,0.5)] transition-all duration-300 hover:-translate-y-1 hover:border-brand-teal/50 hover:shadow-[0_8px_40px_rgba(53,130,124,0.15)]"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
          className="flex h-full flex-col"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue transition-colors group-hover:bg-brand-teal/25">
            <IconByName name={item.icon} className="h-6 w-6" />
          </div>
          <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-white">
            {item.cardLabel || item.label}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.description}</p>
          <span className="mt-auto inline-flex items-center gap-1.5 pt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-blue/90">
            Escolher material
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </motion.div>
      </button>
    );
  }
  return (
    <AccessLink
      href={item.href}
      external={item.external}
      testId={`quick-access-card-${item.id}`}
      onInternalNavigate={(hash) => scrollToSection(hash)}
      badgeClassName="absolute right-5 top-5"
      className="group relative flex h-full flex-col rounded-2xl border border-white/10 bg-[#0d121c]/70 p-6 shadow-[0_4px_24px_rgba(0,0,0,0.5)] transition-all duration-300 hover:-translate-y-1 hover:border-brand-teal/50 hover:shadow-[0_8px_40px_rgba(53,130,124,0.15)] aria-disabled:opacity-70 aria-disabled:hover:translate-y-0"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
        className="flex h-full flex-col"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue transition-colors group-hover:bg-brand-teal/25">
          <IconByName name={item.icon} className="h-6 w-6" />
        </div>
        <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-white">
          {item.label}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.description}</p>
        {item.href && (
          <span className="mt-auto inline-flex items-center gap-1.5 pt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-blue/90">
            {item.external ? "Acessar" : item.id === "contato" ? "Ir para o contato" : "Ver materiais"}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        )}
      </motion.div>
    </AccessLink>
  );
}

export default function QuickAccess() {
  return (
    <section id="acesso-rapido" className="relative scroll-mt-20 py-24 sm:py-28" data-testid="quick-access-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Acesso rápido"
          title="Tudo do seu treino em um só lugar"
          sub="Reunimos os principais links e ferramentas para o seu dia a dia de treino."
          testId="quick-access-heading"
        />
        <div
          className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="quick-access-grid"
        >
          {NAV_ITEMS.map((item, i) => (
            <QuickCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
